import React from 'react';
import styled from 'styled-components';

import { ConditionalLink } from 'src/utils/conditional-link';
import { CarouselV2 } from 'src/components/carousel-v2/carousel-v2';
import type { GetCollectionsNavigationQuery, RadiusOption } from 'src/gql/graphql';
import usePaths from 'src/hooks/use-paths';
import ROUTES from 'src/routes';
import { GridLayout, CarouselLayout } from './navigation.styles';

type CollectionsTextListProps = {
  collections: GetCollectionsNavigationQuery['getCollectionsNavigation'];
  handleCollectionClick: (label: string) => void;
  style: RadiusOption;
};

export const CollectionsTextList = ({
  collections,
  handleCollectionClick,
  style,
}: CollectionsTextListProps): JSX.Element => {
  const { href: baseHref } = usePaths();
  const { COLLECTIONS } = ROUTES;

  const collectionItems = collections.map((collection) => {
    const href = `${baseHref}${COLLECTIONS}${collection.slug}`;
    const title = collection.navigationTitle ?? collection.title;

    return (
      <Item key={collection.id}>
        <ConditionalLink url={href}>
          <Anchor href={href} $radius={style} onClick={() => handleCollectionClick(title)}>
            {title}
          </Anchor>
        </ConditionalLink>
      </Item>
    );
  });

  return (
    <>
      <GridLayout>
        <Title>Collections</Title>
        <Grid>{collectionItems}</Grid>
      </GridLayout>

      <CarouselLayout>
        <CarouselV2 hideTitle mt='0'>
          <Title>Collections</Title>
          {collectionItems}
        </CarouselV2>
      </CarouselLayout>
    </>
  );
};

const Title = styled.h3`
  margin-top: 14px;
`;

const Anchor = styled.a<{ $radius: RadiusOption }>`
  background: #fff;
  border: 2px solid #e3e7e9;
  border-radius: ${({ theme }) => theme.customized.radius.buttons};
  color: #121516;
  display: block;
  font-size: clamp(14px, 2vw, 16px);
  font-weight: 600;
  line-height: 1.5;
  padding: 12px 20px;
  text-align: center;
  transition: border 0.2s;
  white-space: nowrap;
  width: 100%;

  &:hover {
    border-color: #646d72;
  }
`;

const Item = styled.div`
  display: block;

  @media (orientation: landscape) {
    width: 100%;
  }
`;

const Grid = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 24px 0;
`;
